import { createPortal, flushSync } from 'react-dom'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, RefreshCw } from 'lucide-react'
import { useLiveRoom } from '../LiveRoom'
import { loadedRelease, releaseVersion } from '../releaseVersion'
import { refreshBlocker, REFRESH_GUARDS_CHANGED } from '../refreshGuards'
import type { SyncState } from '../types'
import { WorkspaceDialog } from './WorkspaceDialog'

let dismissedRelease: string | null = null

export function ReleaseUpdates({ save, syncState, busy }: { save: () => Promise<boolean>; syncState: SyncState; busy: boolean }) {
  const { connected } = useLiveRoom()
  const [loaded] = useState(loadedRelease)
  const [available, setAvailable] = useState<string | null>(null)
  const [open, setOpen] = useState(false), [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(''), [dismissed, setDismissed] = useState(dismissedRelease)
  const [activity, setActivity] = useState(0)
  const pending = useRef(false), mounted = useRef(true), lastInput = useRef(0), composing = useRef(false)
  const current = useRef({ save, syncState, busy, available, open, dismissed })
  current.current = { save, syncState, busy, available, open, dismissed }
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  useEffect(() => {
    if (!loaded) return // Development pages have no packaged release identity.
    let controller: AbortController | null = null, cancelled = false
    const check = async () => {
      if (document.visibilityState !== 'visible' || controller) return
      controller = new AbortController()
      const timeout = window.setTimeout(() => controller?.abort(), 8000)
      try {
        const response = await fetch('/release.json', { cache: 'no-store', signal: controller.signal })
        if (!response.ok) return
        const version = releaseVersion((await response.json()).commit)
        if (!cancelled && version) setAvailable(version === loaded ? null : version)
      } catch { /* A restarting/offline server is retried on reconnect or focus. */ }
      finally { window.clearTimeout(timeout); controller = null }
    }
    void check()
    const timer = window.setInterval(() => void check(), 30000)
    const visible = () => { void check() }
    window.addEventListener('online', visible); window.addEventListener('focus', visible)
    document.addEventListener('visibilitychange', visible)
    return () => { cancelled = true; controller?.abort(); window.clearInterval(timer); window.removeEventListener('online', visible); window.removeEventListener('focus', visible); document.removeEventListener('visibilitychange', visible) }
  }, [loaded, connected])

  useEffect(() => {
    const changed = () => { lastInput.current = Date.now(); if (current.current.available) setActivity(value => value + 1) }
    const start = () => { composing.current = true; changed() }
    const end = () => { composing.current = false; changed() }
    // Observe after React's editor onChange. A native capture-phase state update
    // can flush a pending render and restore the old controlled value before
    // React reads the newly typed character.
    document.addEventListener('input', changed)
    document.addEventListener('compositionstart', start); document.addEventListener('compositionend', end)
    window.addEventListener(REFRESH_GUARDS_CHANGED, changed)
    return () => { document.removeEventListener('input', changed); document.removeEventListener('compositionstart', start); document.removeEventListener('compositionend', end); window.removeEventListener(REFRESH_GUARDS_CHANGED, changed) }
  }, [])

  const prepare = useCallback(async (reload = false) => {
    if (pending.current || !current.current.available || composing.current) return
    const blocker = refreshBlocker()
    if (blocker || current.current.busy || ['loading', 'conflict'].includes(current.current.syncState)) {
      setMessage(blocker || 'Waiting for your current change to finish.'); return
    }
    pending.current = true; setSaving(true); setMessage('')
    const version = current.current.available, activityAtStart = lastInput.current
    const focused = document.activeElement as HTMLElement | null
    try {
      // Renames finish linked-mention updates on blur. Commit that change before
      // asking persistence to confirm the latest writing is durable.
      flushSync(() => { if (focused?.matches('input, textarea, [contenteditable="true"]')) focused.blur() })
      const saved = await current.current.save()
      if (!mounted.current || current.current.available !== version) return
      if (!saved) { setMessage('Your writing still needs to save. Keep working here; the update can wait.'); return }
      if (refreshBlocker() || current.current.busy || composing.current || lastInput.current !== activityAtStart) {
        setMessage('Waiting for your latest change to finish.'); return
      }
      if (reload) window.location.reload() // Preserve every current route parameter.
      else setOpen(true)
    } catch { if (mounted.current) setMessage('Your writing could not be confirmed as saved. Try again when you are connected.') }
    finally {
      pending.current = false
      if (mounted.current) setSaving(false)
      if (document.activeElement === document.body && focused?.isConnected) focused.focus({ preventScroll: true })
    }
  }, [])

  useEffect(() => {
    if (!available) { setOpen(false); setMessage(''); return }
    // Autosave owns retries. A failed/offline save must not create a prompt loop.
    if (open || available === dismissed || syncState !== 'saved' || busy) return
    const timer = window.setTimeout(() => { void prepare() }, Math.max(800 - (Date.now() - lastInput.current), 0))
    return () => window.clearTimeout(timer)
  }, [available, dismissed, open, syncState, busy, activity, prepare])
  if (!available) return null
  const later = () => { if (!saving) { dismissedRelease = available; setDismissed(available); setOpen(false) } }
  return createPortal(<>
    {!open && <div className="release-reminder" role="status">
      <button type="button" className="button" disabled={saving} onClick={() => { void prepare() }}><RefreshCw size={15} className={saving ? 'spin' : ''} />{saving ? 'Checking your save…' : 'Update ready'}</button>
      {message && <small>{message}</small>}
    </div>}
    {open && <WorkspaceDialog title="New updates are live" eyebrow="A FRESH BRAMBLE" className="release-dialog" initialFocus="close" onClose={later}>
      <div className="release-body"><p><Check size={18} />{syncState === 'saved' && !message ? 'Your writing is saved.' : 'We’ll check your latest save before refreshing.'}</p><p>Refresh to load the latest Bramble. You’ll return to this same view.</p>
        {message && <p role="alert">{message}</p>}
      </div>
      <footer className="workspace-dialog-actions"><button type="button" className="button" disabled={saving} onClick={later}>Later</button>
        <button type="button" className="button button--primary" disabled={saving} onClick={() => { void prepare(true) }}><RefreshCw size={15} className={saving ? 'spin' : ''} />{saving ? 'Checking your save…' : 'Refresh now'}</button>
      </footer>
    </WorkspaceDialog>}
  </>, document.body)
}
